/**
 * Kural motorunun tum girdi/cikti tipleri.
 *
 * KURALLAR:
 *  - Bu dosya ve bu paket BAGIMSIZDIR. Hicbir package, framework veya I/O import edilmez.
 *  - Tum fonksiyonlar saf (pure) ve deterministiktir: ayni girdi = ayni cikti.
 *  - Zar atma tek istisnadir ve o da acikca bir `Rng` parametresi alir.
 */

/** Desteklenen kural seti surumu. Yeni surum eklendiginde burasi genisler. */
export const RULESETS = ['5.1', '5.2', 'homebrew'] as const
export type Ruleset = (typeof RULESETS)[number]

/** D&D 5e (SRD 5.1) icin varsayilan. */
export const DEFAULT_RULESET: Ruleset = '5.1'

// ---------------------------------------------------------------------------
// Yetenekler & beceriler
// ---------------------------------------------------------------------------

export const ABILITY_KEYS = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const
export type AbilityKey = (typeof ABILITY_KEYS)[number]

export const ABILITY_NAMES_TR: Record<AbilityKey, string> = {
  str: 'Güç',
  dex: 'Çeviklik',
  con: 'Dayanıklılık',
  int: 'Zekâ',
  wis: 'Algı',
  cha: 'Karizma',
}

export interface SkillDef {
  key: string
  ability: AbilityKey
  label: string
  labelTr: string
}

/** 18 temel beceri. D&D 5e SRD 5.1. */
export const SKILLS: readonly SkillDef[] = [
  { key: 'acrobatics', ability: 'dex', label: 'Acrobatics', labelTr: 'Akrobasi' },
  { key: 'animalHandling', ability: 'wis', label: 'Animal Handling', labelTr: 'Hayvanla Uğraşma' },
  { key: 'arcana', ability: 'int', label: 'Arcana', labelTr: 'Arkan Bilgi' },
  { key: 'athletics', ability: 'str', label: 'Athletics', labelTr: 'Atletik' },
  { key: 'deception', ability: 'cha', label: 'Deception', labelTr: 'Aldatma' },
  { key: 'history', ability: 'int', label: 'History', labelTr: 'Tarih' },
  { key: 'insight', ability: 'wis', label: 'Insight', labelTr: 'Sezgi' },
  { key: 'intimidation', ability: 'cha', label: 'Intimidation', labelTr: 'Caydırma' },
  { key: 'investigation', ability: 'int', label: 'Investigation', labelTr: 'Soruşturma' },
  { key: 'medicine', ability: 'wis', label: 'Medicine', labelTr: 'Tıp' },
  { key: 'nature', ability: 'int', label: 'Nature', labelTr: 'Doğa Bilgisi' },
  { key: 'perception', ability: 'wis', label: 'Perception', labelTr: 'Algı' },
  { key: 'performance', ability: 'cha', label: 'Performance', labelTr: 'Performans' },
  { key: 'persuasion', ability: 'cha', label: 'Persuasion', labelTr: 'Ikna' },
  { key: 'religion', ability: 'int', label: 'Religion', labelTr: 'Din' },
  { key: 'sleightOfHand', ability: 'dex', label: 'Sleight of Hand', labelTr: 'Çevik El' },
  { key: 'stealth', ability: 'dex', label: 'Stealth', labelTr: 'Gizli' },
  { key: 'survival', ability: 'wis', label: 'Survival', labelTr: 'Hayatta Kalma' },
] as const

export type SkillKey = string

// ---------------------------------------------------------------------------
// Icek icerik tanimlari (veritabanindan gelen kucuk, indekslenmis sekiller)
// ---------------------------------------------------------------------------

export type ArmorProficiency = 'light' | 'medium' | 'heavy' | 'shields'

export interface ItemBonus {
  ability?: Partial<Record<AbilityKey, number>>
  skill?: Record<string, number>
  savingThrow?: Partial<Record<AbilityKey, number>>
  armorClass?: number
  initiative?: number
  proficiencyBonus?: number
  hitPoints?: number
}

export interface ItemEntry {
  id: string
  name: string
  itemType: string
  rarity: string
  requiresAttunement: boolean
  attunementMax: number

  // --- Zirh ---
  /** Zirhin kendi taban AC'si (bonus haric). Or. zincir zirhi 16. */
  armorClass?: number
  /** Zirhin bonusu. Or. +1 zirh = 1. Toplam = armorClass + min(armorBonus, maxBonus). */
  armorBonus?: number
  armorType?: ArmorProficiency
  /** Ceviklik bu zirhla ne kadar katkida bulunur. */
  dexMode?: 'full' | 'half' | 'none'
  /** Kaldirma icin gereken minimum GUC. null = kisitlama yok. */
  strengthRequirement?: number | null
  stealthDisadvantage?: boolean
  /** Bonus tavanai (asla gecilmez). Or. +1 zirh icin 1, +4 zirh yine 3. */
  maxBonus?: number

  // --- Kalkan ---
  /** Kalkan AC bonusu. Genelde 2. */
  shieldBonus?: number

  chargesMax?: number
  /** Agirlik (adet basina). */
  weight?: number
  weapon?: {
    damage: string
    damageType: string
    isFinesse?: boolean
    isHeavy?: boolean
    isTwoHanded?: boolean
    isVersatile?: boolean
    versatileDamage?: string
    reach?: number
    thrown?: boolean
  }
  bonuses?: ItemBonus[]
}

export interface FeatureEntry {
  id: string
  name: string
  description?: string
  /**
   * Hangi seviyede (ve sonrasında) açılır.
   * Sınıf özelliklerinde seviye zaten `class_level` satırından gelir; IRK
   * özelliklerinde ise burada belirtilir (örn. Seviye 3 — Drakonik Direnç).
   * undefined = 1. seviyeden itibaren.
   */
  grantedAtLevel?: number
  /** Kalici AC bonusu (ornek: Elegy's Armor +2). */
  acBonus?: number
  /**
   * Bu AC bonusu YALNIZCA zirh giydiginde gecerlidir.
   * 2024 Silah Ustaligi "Savunma" icin sarttir: "+1 AC *zırh giyenken*".
   * Ağaç İnsanı gibi zırhsız AC formulu olan ırklarda yanlışlıkla
   * uygulanmasin diye acikca isaretlenir.
   */
  requiresArmor?: boolean
  /** Bonus sayisindaki ASLA gecilmeyecek tavan (Light Foot - sadece 1). */
  acBonusCap?: number
  /** Savunma tipi. Cevik zirh yoksa nasil hesaplanacagi. */
  defenseMode?: 'unarmoredDex' | 'unarmoredDexCon'
  abilityBonuses?: Partial<Record<AbilityKey, number>>
  skillBonuses?: Record<string, number>
  savingThrowProficiencies?: AbilityKey[]
  savingThrowBonuses?: Partial<Record<AbilityKey, number>>
  damageResistances?: string[]
  damageImmunities?: string[]
  damageVulnerabilities?: string[]
  conditionImmunities?: string[]
  grantsProficiencies?: string[]
  /** Dogrudan büyü yuvası tablosu (ör. Feature: basamak/ek büyücülük). */
  spellSlotsOverride?: Record<string, number>
  /** Yuva harcamadan kullanilabilen büyüler (örn. Büyüdoğmuş, Bitki Halkı). */
  innateSpells?: InnateSpellGrant[]
  /** Dogal silah (pençe, bot, boynuz). */
  naturalWeapon?: NaturalWeapon
}

// ---------------------------------------------------------------------------
// OYUNCU SEÇİMLERİ (1/N veya N/M)
//
// Örnekler:
//   Ejder Soylu    : 1 / 5  -> hasar türü (Acid, Cold, Fire, Lightning, Poison)
//   Ağaç İnsanı    : 1 / 3  -> soy (Meşe, Söğüt, Çiçek/Meyve)
//   Kristal Halkı  : 1 / 2  -> kristal tipi
//   Mutant         : 2 / 8  -> adaptasyon  +  1 / 5 -> bedel
//   Hayvan Halkı   : 2 / 6  -> doğal özellik
//
// KURAL: Seçim yapılmamışsa o seçimin etkileri UYGULANMAZ ve sheet'te
// "seçilmedi" olarak uyarı üretilir. Motor sessizce varsayılan seçmez.
// ---------------------------------------------------------------------------

export interface ChoiceEffects {
  abilityBonuses?: Partial<Record<AbilityKey, number>>
  skillProficiencies?: string[]
  toolProficiencies?: string[]
  languageProficiencies?: string[]
  savingThrowProficiencies?: AbilityKey[]
  grantsProficiencies?: string[]
  damageResistances?: string[]
  damageImmunities?: string[]
  damageVulnerabilities?: string[]
  conditionImmunities?: string[]
  acBonus?: number
  acBonusCap?: number
  /** Zirhsiz AC tabanı. Varsayılan 10. Ağaç İnsanı: 13. */
  acBase?: number
  defenseMode?: 'unarmoredDex' | 'unarmoredDexCon'
  speedBonus?: number
  size?: string
  reachBonus?: number
  darkvision?: number
  cantrips?: string[]
  innateSpells?: InnateSpellGrant[]
  /** Verilen feat kimlikleri (örn. İnsan → 1 Origin Feat). */
  feats?: string[]
  naturalWeapon?: NaturalWeapon
  /** Secilen hasar türü (Ejder Nefesi). */
  damageType?: string
  spellSlotsOverride?: Record<string, number>
}

export interface ChoiceOption {
  id: string
  name: string
  nameTr: string
  description: string
  effects: ChoiceEffects
}

export interface ChoiceGroup {
  id: string
  label: string
  labelTr: string
  description: string
  /** Kac secim yapilacak. `min` 1 ise tek secim, >1 ise coklu secim. */
  min: number
  /** En fazla kac secim. min === max ise tam olarak o kadar secilir. */
  max: number
  options: ChoiceOption[]
  /** Hangi seviyeden itibaren secilir. undefined = 1. seviye. */
  grantedAtLevel?: number
  /**
   * İÇERİK EKSİK: Bu grubun seçenek listesi henüz yazılmadı.
   *
   * Neden var: Bazı ırklar "listedeki 2 özellikten birini seç" der ama liste
   * kural metninde yok. Bu durumda seçenek UYDURMAK, DM'in kendi sisteminde
   * resmî gibi görünen sahte mekanikler yaratır. Doğrusu: yapıyı kur, listeyi
   * bekle, arayüzde "liste bekleniyor" de.
   *
   * Etkisi: `pending: true` olan grup KULLANICI HATASI UYARISI ÜRETMEZ —
   * çünkü eksiklik içerikte, oyuncuda değil.
   */
  pending?: boolean
  /** pending grubunda listede beklenen seçenek sayısı (UI ipucu için). */
  pendingCount?: number
}

// ---------------------------------------------------------------------------
// Yuva HARCAMADAN kullanilabilen buyuler
// (Büyüdoğmuş, Bitki Halkı, Balık Adamlar, Ağaç İnsanı)
//
// DİKKAT: Bu, "ek yuva" DEĞİLDİR. Oyuncunun yuvası harcanmaz, ayrı bir
// kullanım sayacı vardir. 5e'de yuva tablosuyla aritmetik yapilmaz; bu yuzden
// model yalnizca KULLANILABILIRLIK ve gosterim icindir.
// ---------------------------------------------------------------------------

export interface InnateSpellGrant {
  /** Bu buyu hangi seviyede ogrenilir. */
  grantedAtLevel: number
  spellIds: string[]
  uses: { count: number; recharge: 'short' | 'long' }
}

// ---------------------------------------------------------------------------
// Dogal silah (pençe, bot, boynuz, kuyruk)
// ---------------------------------------------------------------------------

export interface NaturalWeapon {
  name: string
  /** Hasar zari. Ornek "1d6". */
  dice: string
  damageType: string
  /** Hasar hangi yetenekten gider. */
  ability: AbilityKey
  /** Cevik: STR veya DEX'ten daha iyisi kullanilir. */
  finesse?: boolean
  /**
   * "En az" kurali: bu zar veya daha buyugu kullanilir.
   * Ornek (Hayvan Halkı İlkel Form): hasar zari EN AZ 1d8 olur.
   */
  minimumDice?: string
}

export interface CantripStep {
  level: number
  count: number
}

export type SpellcastingSpec =
  | { mode: 'none' }
  | {
      mode: 'full' | 'half'
      ability: AbilityKey
      preparation: 'prepared' | 'known'
      cantripProgression: CantripStep[]
    }
  | {
      mode: 'artificer'
      ability: AbilityKey
      preparation: 'prepared'
      cantripProgression: CantripStep[]
    }

export interface ClassLevelEntry {
  level: number
  features: FeatureEntry[]
  /** Anahtar: büyü yuvası seviyesi (metin), deger: adet. */
  spellSlots: Record<string, number>
  /** 3. seviyede 1, 4. seviyede 2 ... */
  asi: number
  subclassLevel?: number
  subclassId?: string
}

export interface ClassEntry {
  id: string
  name: string
  hitDie: number
  primaryAbility: AbilityKey
  savingThrowProficiencies: AbilityKey[]
  armorProficiencies: ArmorProficiency[]
  weaponProficiencies: string[]
  /**
   * Seviye 1'de seçilebilecek beceriler (çıplak anahtarlar: "athletics",
   * "insight" …).
   *
   * Neden ayrı alan: 5e metni bu listeyi sınıf açıklamasında yazar ama
   * mekaniksel değildir. Özelliğin `description` metnine gömülürse sihirbaz
   * havuzu okuyamaz. Açık bir alan = sihirbaz listeleyebilir, doğrulayabilir.
   */
  skillChoicePool: string[]
  skillChoices: number
  spellcasting: SpellcastingSpec
  levels: ClassLevelEntry[]
}

export interface RaceEntry {
  id: string
  name: string
  /** Zarf ölçüsü: "Küçük" | "Orta" | "Büyük" | "Dev" */
  size: string
  /** Menzil (ft). Varsayılan 5. */
  reach: number
  /** Temel hareket hızı. */
  speed: number
  /** Irkin SABIT verdugu bonuslar. Or. Elf +2 DEX. */
  abilityBonuses: Partial<Record<AbilityKey, number>>
  /** Oyuncunun sectigi ek bonus (Irk varyanti +1, gelenek, ASI). */
  asiChoices: Partial<Record<AbilityKey, number>>
  /**
   * Oyuncunun +1 (veya benzeri) bonusu HANGI yeteneklere koyabileceği.
   * 5e Elf'i: "+2 DEX, +1 Zekâ/Algı/Karizma" → ['int','wis','cha'].
   * Boş dizi = oyuncu seçimi yok, bonus sabittir.
   */
  asiChoiceOptions: AbilityKey[]
  /** Ek yonetim noktasi sayisi (Asi seviyesine eklenir). */
  asi: number
  /** Zirhsiz AC tabani. Varsayılan 10. Ağaç İnsanı: 13. */
  acBase: number
  darkvision: number
  /** Irkin verdigi cantrip'ler (orn. Yuksek Elf: Druidcraft). */
  cantrips: string[]
  /** Irk veren feat (orn. İnsan → 1 Origin Feat). */
  feat: string | null
  /** Oyuncunun yapmasi gereken secimler. */
  choices: ChoiceGroup[]
  /** Irkin verdigi, yuva harcamadan kullanilan buyuler. */
  innateSpells: InnateSpellGrant[]
  features: FeatureEntry[]
}

// ---------------------------------------------------------------------------
// ÖZEL KAYNAK (KP gibi) — 5e'de standart yok, ev sistemi için.
//
// Neden ayrı tip: bir kaynak "verilen" ile "harcanan" iki ayrı şeydir.
// `ResourceDef` verilen şeyi tanımlar (ad, azami, yenilenme).
// Anlık değer ise OTURUM durumundadır (`SessionState.pools`) — çünkü
// kural motoru hesaplanmışı saklamaz, yalnızca girdiyi saklar.
// ---------------------------------------------------------------------------

/**
 * Seviyeyle büyüyen kaynak kuralı (Vampir KP gibi).
 *
 * NEBEN SABİT SAYI DEĞİL: Ev sistemi KP'yi seviyeye bağlıyor. Karakter
 * seviye yükseldikçe azami KP artar, böylece eski karakterler güçlenmiş
 * olmaz ve yeni oyuncular yakalayabilir. Sabit bir `max` yazmak bu
 * davranışı imkânsız kılardı.
 */
export interface ResourceScaling {
  /**
   * Kaynak hangi seviyeden itibaren VAR OLUR.
   * Vampir KP = 3 → 1. ve 2. seviyede KP hiç görünmez.
   *
   * Neden görünmez, sıfır mı yazılır: 1. seviyede "0/3 KP" yazan bir bar
   * oyuncuya "bu karaktere yol vermem gerekiyor" der. Hiç göstermek
   * aynı bilgiyi daha temiz verir.
   */
  startsAtLevel: number
  /** `startsAtLevel` seviyesindeki azami değer. */
  baseMax: number
  /**
   * Her seviye artışında eklenen miktar. 0 = sabit (sadece başlangıçta doğar).
   * Vampir KP: 3. sv 3, 4. sv 4, 5. sv 5 …
   */
  perLevel?: number
  /**
   * Düzensiz büyüme için AÇIK TABLO. Varsa `perLevel` yok sayılır.
   * `{ 3: 3, 5: 4, 9: 5 }` → 3-4. sv 3, 5-8. sv 4, 9+. sv 5
   *
   * Neden gerekli: "her seviye +1" ev sistemlerinde hızlı büyüme yapar
   * (17. seviyede 17 KP). Bir masada bazen "3. sv 2, 6. sv 3, 9. sv 4"
   * gibi kademeli artış istenir. İki biçimi de destekliyoruz ki içerik
   * yazarken hangisi uygunsa onu seçsin.
   */
  steps?: Record<string, number>
  /** Tavan. `undefined` = tavan yok. */
  capMax?: number
}

export interface ResourceDef {
  id: string
  name: string
  /**
   * SABİT azami değer. `scaling` verildiginde yok sayilir.
   *
   * Kural: ikisinden EN AZ BIRI tanimli olmalidir; motor dogrular.
   */
  max?: number
  /** Seviyeyle buyuyen kaynak. Vampir KP gibi. */
  scaling?: ResourceScaling
  /** Ne zaman doluyor. */
  recharge: 'short' | 'long' | 'manual'
  description?: string
  /** Kağıtta can barının YANINDA gösterilsin mi? (KP için evet) */
  showNextToHitPoints?: boolean
}

/**
 * Kaynagin, verilen seviyedeki AZAMI degerini hesaplar.
 *
 * `undefined` donerse o seviyede kaynak HENUZ YOKTUR (startsAtLevel
 * kapiyi gecmedi). Bu ayrim onemli: "0" degil "yok" demektir, arayuz
 * farkli davranir.
 */
export function resolveResourceMax(resource: ResourceDef, level: number): number | undefined {
  if (resource.scaling) {
    const { startsAtLevel, baseMax, perLevel = 0, steps, capMax } = resource.scaling
    if (level < startsAtLevel) return undefined

    let value: number
    if (steps && Object.keys(steps).length > 0) {
      // En YUKSEK uygulanan esik: esikler 3,5,9 ise 5. seviyede 5 olur.
      value = baseMax
      for (const [levelText, amount] of Object.entries(steps)) {
        const threshold = Number(levelText)
        if (Number.isFinite(threshold) && level >= threshold) value = amount
      }
    } else {
      value = baseMax + (level - startsAtLevel) * perLevel
    }

    if (capMax != null) value = Math.min(value, capMax)
    return Math.max(0, Math.round(value))
  }

  return resource.max
}

// ---------------------------------------------------------------------------
// ÖZEL YOL — GM'in yazdığı, seviyeli özelliklerden oluşan paket.
//
// Neden ayrı bir kavram: 5e'de "yol" (path) sınıfın alt sınıfıdır ve
// TEK seçilir. Bu ev sistemi farklı: bir karakter istedigi kadar yol
// alabilir (vampir yollari, ozan yollari, ek gelenekler...). Bu yuzden
// yol bir SEÇIM DEĞIL, bir KATMAN: her yol kendi ozelliklerini ekler ve
// hicbiri digerinin yerine gecmez.
//
// ESLEME KURALLARI (motor bunu zorunlu uygular):
//   - Yol ozellikleri seviyeye baglidir (`FeatureEntry.grantedAtLevel`).
//     3. seviyede acilan bir yol ozelligi 1. seviyede UYGULANMAZ.
//   - Iki yol ayni ozelligi verirse AC/bonus degerlerinden buyugu alinir
//     (`spellSlotsOverride` ile ayni kural), ozellik listesi ikisini de gosterir.
//   - Bilinmeyen yol kimligi sessizce yutulmaz; `warnings` icinde bildirilir.
// ---------------------------------------------------------------------------

export interface PathEntry {
  id: string
  name: string
  description?: string
  /**
   * Bu yolun ait oldugu irk. Bos/null = HER IRKE acik.
   * Arayuz yalnizca karakterin irkina ait yollari onerecek ama sinirlamaz.
   */
  raceId?: string | null
  /** Seviyeli ozellikler. */
  features: FeatureEntry[]
  /** Yolun verdigi ozel kaynak (orn. Vampir KP). Birden fazla yol ayni
   *  kaynagi verirse birlesir. */
  resources?: ResourceDef[]
  /**
   * Kac yol alinabilecegini sinirla. `undefined` = SINIRSIZ.
   *
   * Neden sinir var: ev sistemi "istedigi kadar" diyor, ama bir karakterin
   * 40 yolu olmasi icerik hatasi olabilir. Bu deger yalnizca bir TAVAN;
   * varsayilan sinirsizdir.
   */
  maxPaths?: number
}

// ---------------------------------------------------------------------------
// Karakter girdisi
// ---------------------------------------------------------------------------

export interface BackgroundEntry {
  id: string
  name: string
  skillProficiencies: string[]
  toolProficiencies: string[]
  languages: string[]
  features: FeatureEntry[]
  gold: number
}

export interface SpellEntry {
  id: string
  name: string
  level: number
  school: string
  castingTime: string
  range: string
  ritual: boolean
  concentration: boolean
  classes: string[]
  damage?: {
    type: string
    dice: string
    atHigherLevels?: { level: number; dice: string }[]
  }
}

export interface ContentIndex {
  classes: Record<string, ClassEntry>
  races: Record<string, RaceEntry>
  backgrounds: Record<string, BackgroundEntry>
  items: Record<string, ItemEntry>
  spells: Record<string, SpellEntry>
  features: Record<string, FeatureEntry>
  /** Ozel yollar. `CharacterInput.paths` bu indekse bakar. */
  paths: Record<string, PathEntry>
}

// ---------------------------------------------------------------------------
// Karakter girdisi
// ---------------------------------------------------------------------------

export interface ClassAssignment {
  classId: string
  level: number
  subclassId?: string | null
  isPrimary?: boolean
  /** Toplam can puani elle override edilebilir (max HP degisikligi, vampir, vb). */
  hitPointsOverride?: number | null
}

export interface EquippedItem {
  itemId: string
  quantity: number
  attuned: boolean
  /** Cevik zirh icin: uygulanacak azami bonus (orn. higir boynuzu). */
  dexAcCap?: number | null
  equipSlot?: string | null
  chargesUsed?: number
  /** Agirlik (adet basina). */
  weight?: number
}

export interface CharacterInput {
  id?: string
  ruleset: Ruleset
  level: number
  xp?: number
  classes: ClassAssignment[]
  raceId?: string | null
  backgroundId?: string | null
  /** Temel (base) puanlar. Irk bonuslari motor tarafindan eklenir. */
  abilityScores: Record<AbilityKey, number>
  /**
   * Oyuncunun secimine bagli yetenek bonuslari.
   * Kaynaklar: Irk varyanti (orn. Elf +1 INT/WIS/CHA), alinan Ability Score
   * Improvement'ler, Feat'ler. Motor bunlari `abilityScores` uzerine ekler.
   */
  asiBonuses?: Partial<Record<AbilityKey, number>>
  /** 'skill.acrobatics' | 'save.dex' | 'tool.vehicle' | 'language.dwarven' */
  proficiencies: string[]
  inventory: EquippedItem[]
  conditions: string[]
  tempHitPoints?: number
  /**
   * Yapilan oyuncu secimleri.
   * Anahtar = `ChoiceGroup.id`, deger = secilen `ChoiceOption.id` listesi.
   *
   * Ornek (Ejder Soylu):
   *   choices: { 'ejder-nefesi-tur': ['fire'] }
   *
   * KURAL: Bir secimin etkileri YALNIZCA burada listelenen secenekler icin
   * uygulanir. Eksik/yanlis secim sessizce yoksayilmaz; `ComputedSheet.warnings`
   * icinde bildirilir.
   */
  choices?: Record<string, string[]>
  /**
   * Alinan ozel yollar. `ContentIndex.paths` icindeki `PathEntry.id` listesi.
   *
   * SINIRSIZ: kac yol alinacagi `PathEntry.maxPaths` ile sinirlanir; hicbir
   * yol bu degeri tanimlamiyorsa sinir yoktur. Motif: vampir yollari,
   * ozan yollari, ek gelenekler — hepsi katman olarak üst üste biner.
   */
  paths?: string[]
  /**
   * OZEL EK KAYNAK BONUSU — anahtar `ResourceDef.id`, deger ek azami.
   *
   * Ev sisteminin kurali: kaynak taban degerden gelir (seviyeye gore),
   * ama GM istedigi karaktere USTUNE ekleyebilir. Ornek: taban 3 KP,
   * GM "bu karakter 2 fazla KP harcar" derse deger 5 olur.
   *
   * NEDEN GIRDE YAZAR, OTURUM DURUMUNDA DEGIL: azami degeri MOTOR hesaplar.
   * "1 + bonus = 2" kurali tek yerde olmali; iki yere birden yazilirsa
   * arayuz ile motor birbirinden ayrilir ve hangisinin dogru oldugu
   * belirsizlasir. Bonus giridir, hesaplanmis sonuc degildir.
   */
  resourceBonuses?: Record<string, number>
}

// ---------------------------------------------------------------------------
// Hesaplanmis sheet ciktisi
// ---------------------------------------------------------------------------

export interface AbilityResult {
  score: number
  modifier: number
  saveBonus: number
  proficient: boolean
}

export interface SkillResult {
  key: string
  label: string
  labelTr: string
  ability: AbilityKey
  proficient: boolean
  modifier: number
  passive: number
}

export interface SpellSlotSummary {
  level: number
  total: number
  used: number
  remaining: number
}

export interface SpellcastingResult {
  ability: AbilityKey
  abilityModifier: number
  saveDc: number
  attackBonus: number
  preparation: 'prepared' | 'known'
  cantripsKnown: number
  preparedCount: number
  slots: SpellSlotSummary[]
  isSpellcaster: boolean
}

export interface DefenseResult {
  armorClass: number
  initiative: number
  speed: number
  hitPoints: number
  tempHitPoints: number
  carryCapacity: number
  equippedArmor: string | null
  dexAcCap: number | null
  /** Zirhsiz AC tabani (Ağaç İnsanı: 13, diğerleri 10). */
  acBase: number
}

export interface ResolvedFeature {
  id: string
  name: string
  source: 'class' | 'race' | 'background' | 'item' | 'feat' | 'condition' | 'path'
  sourceName: string
  level?: number
}

/** Sheet'te "Yollarım" bolumunde gosterilecek tek bir yol ozeti. */
export interface ResolvedPath {
  id: string
  name: string
  description?: string
  /** Bu yolun TUM ozellikleri (seviye filtresi uygulanmadan once). */
  allFeatures: { id: string; name: string; grantedAtLevel: number }[]
  /** Karakterin seviyesi acilanlar. */
  activeFeatures: { id: string; name: string; grantedAtLevel: number }[]
  /** Henuz acilmamis ozellikler — sheet'te "3. seviyede acilir" diye gosterilir. */
  lockedFeatures: { id: string; name: string; grantedAtLevel: number }[]
  /** Bu yolun verdigi kaynaklar (orn. KP). */
  resources: ResourceDef[]
}

export interface ResolvedChoice {
  groupId: string
  groupLabel: string
  groupLabelTr: string
  /** Gercekten uygulanan secenekler. */
  selected: { id: string; nameTr: string }[]
  /** Secilmesi gereken toplam (grubun min degeri). */
  required: number
  /** En fazla secilebilecek sayi (grubun max degeri). */
  max: number
  /** Kac tanesi secildi. */
  chosen: number
  /** Henuz yeterli secim yapilmadi mi? */
  complete: boolean
  /** Bu grup henuz acildi mi (seviye kosulu)? */
  unlocked: boolean
  /** Liste bekleniyor mu (icerik eksik)? */
  pending: boolean
  /** pending grupta listede beklenen secenek sayisi. */
  pendingCount: number
  /** Secilebilecek tum secenekler (arayuz listeleyebilsin diye). */
  available: { id: string; nameTr: string }[]
}

export interface InnateSpellSummary {
  spellIds: string[]
  grantedAtLevel: number
  uses: { count: number; recharge: 'short' | 'long' }
}

export interface ComputedSheet {
  ruleset: Ruleset
  level: number
  xp: number
  proficiencyBonus: number
  asiEarned: number
  abilities: Record<AbilityKey, AbilityResult>
  skills: SkillResult[]
  savingThrows: Record<AbilityKey, number>
  defense: DefenseResult
  spellcasting: SpellcastingResult
  attackBonus: number
  passivePerception: number
  passiveInvestigation: number
  passiveInsight: number
  senses: { darkvision: number }
  resistances: {
    damageResistances: string[]
    damageImmunities: string[]
    damageVulnerabilities: string[]
    conditionImmunities: string[]
  }
  attunement: { used: number; max: number }
  features: ResolvedFeature[]
  asiChoicesRemaining: number
  conditions: AppliedCondition[]
  attacks: AttackResult[]
  /** Irktan gelen dogal silahlar (pençe, bot, boynuz). */
  naturalWeapons: AttackResult[]
  /** Yuva harcamadan kullanilabilen buyuler. */
  innateSpells: InnateSpellSummary[]
  /** Cozulmus oyuncu secimleri (arayuz bunu doldurur). */
  choices: ResolvedChoice[]
  /** Alinan ozel yollar ve ozellikleri. */
  paths: ResolvedPath[]
  /**
   * Karaktere verilen ozel kaynaklar (KP vb) ve AZAMI degerleri.
   *
   * `max` burada ZORUNLUDUR ve her zaman hesaplanmis gelir:
   *   azami = seviyeye gore taban + CharacterInput.resourceBonuses bonusu
   *
   * Kaynak o seviyede henuz yoksa (`scaling.startsAtLevel` gecilmemis)
   * LISTEDE BULUNMAZ — "0/3" degil, hic gosterilmez. Boylece oyuncu
   * "once yol almam gerekiyor" mesajini alir.
   *
   * Anlik deger (kac KP kaldi) OTURUM durumundadir, burada degil.
   */
  resources: (ResourceDef & { max: number })[]
  /** Zarf olcusu ve menzil. */
  size: string
  reach: number
  inventory: InventoryLine[]
  weight: { total: number; carryCapacity: number }
  warnings: string[]
}

export interface AppliedCondition {
  slug: string
  label: string
  labelTr: string
  canMove: boolean
  canSpeak: boolean
  canSee: boolean
  canAct: boolean
  canAttack: boolean
  /** Saldiriya gelen bonus. */
  incomingAttack: 'none' | 'advantage' | 'disadvantage'
  /** Kendi saldirlari. */
  ownAttack: 'none' | 'advantage' | 'disadvantage'
}

export interface AttackResult {
  itemId: string
  name: string
  isRanged: boolean
  rangeShort: number
  rangeLong: number | null
  /** To attack'a eklenen bonus. */
  attackBonus: number
  /** Hasar zari (TABAN). Or. "1d8". Bonus ayrica tutulur. */
  damageDice: string
  damageBonus: number
  damageType: string
  isFinesse: boolean
  isVersatile: boolean
  versatileDamageDice: string | null
  /** Ek notlar: ustalik, temel durum vs. */
  notes: string[]
}

export interface InventoryLine {
  itemId: string
  name: string
  quantity: number
  weightEach: number
  requiresAttunement: boolean
  attuned: boolean
  equipped: boolean
  slot: string | null
  chargesUsed: number
  chargesMax: number
}
